import React, { useMemo } from 'react'
import { Box, Button, FormHelperText, Grid } from '@mui/material'
import { Form, Formik } from 'formik'
import dayjs from 'dayjs'
import {
  AdGroupBudgetPlanPartsFragment,
  AdGroupBudgetPlansDocument,
  AdGroupCreateInput,
  AdGroupInput,
  CostType,
  useCreateAdGroupBudgetPlanMutation,
  useUpdateAdGroupBudgetPlanMutation,
} from '../../../../../__generated__/graphql'
import DateField from '../../../../../components/form/DateField'
import { StyledFormLabel } from '../../../../../components/form/styled'
import { useAdvertiserConfigContext } from '../../../../../utils/hooks/advertiser/useAdvertiserConfigContext'
import { useAppTranslations } from '../../../../../utils/hooks/useAppTranslations'
import { initialTouchedForAll } from '../../../../../utils/initialTouchedForAll'
import { EditionModeContextProvider, EditionModes } from '../../../../../utils/providers/editionModeContext'
import { useSnackbarManager } from '../../../../../utils/providers/snackbarContext'
import { BudgetAndDates } from '..'
import {
  BudgetPlanFormValues,
  emptyBudgetPlanFormValues,
  existingPlanDateKeys,
  toAdGroupBudgetPlanCreateInput,
  toAdGroupBudgetPlanUpdateInput,
  toBudgetPlanFormValues,
  tomorrowUtcDateOnly,
} from './budgetPlanForm.utils'
import { budgetPlanSchema } from './budgetPlanSchema'

interface BudgetPlanFormProps {
  adGroupId: string
  adgroup: AdGroupInput | AdGroupCreateInput
  plans: AdGroupBudgetPlanPartsFragment[]
  editingPlan?: AdGroupBudgetPlanPartsFragment
  onDone?: () => void
}

const BudgetPlanForm: React.FC<BudgetPlanFormProps> = ({
  adGroupId,
  adgroup,
  plans,
  editingPlan,
  onDone,
}): JSX.Element => {
  const t = useAppTranslations()
  const [, setSnackbar] = useSnackbarManager()
  const {
    config: { adGroup: adgroupConf },
  } = useAdvertiserConfigContext()
  const mutationOptions = {
    refetchQueries: [{ query: AdGroupBudgetPlansDocument, variables: { id: adGroupId } }],
    awaitRefetchQueries: true,
  }
  const [createPlan, { loading: createLoading }] = useCreateAdGroupBudgetPlanMutation(mutationOptions)
  const [updatePlan, { loading: updateLoading }] = useUpdateAdGroupBudgetPlanMutation(mutationOptions)
  const loading = createLoading || updateLoading
  const isEditing = editingPlan != null

  const existingPlannedDates = useMemo(
    () => existingPlanDateKeys(isEditing ? plans.filter((plan) => plan.id !== editingPlan.id) : plans),
    [editingPlan, isEditing, plans]
  )
  const currentCostType = (adgroup.costType as CostType | undefined) ?? undefined
  const validationSchema = useMemo(
    () => budgetPlanSchema(adgroupConf, { existingPlannedDates, currentCostType, t }),
    [adgroupConf, currentCostType, existingPlannedDates, t]
  )

  const initialValues = useMemo(
    () => (isEditing ? toBudgetPlanFormValues(editingPlan) : emptyBudgetPlanFormValues()),
    [editingPlan, isEditing]
  )

  const initialTouched = useMemo(() => initialTouchedForAll({ ...initialValues }), [initialValues])

  const handleSubmit = async (
    values: BudgetPlanFormValues,
    { resetForm }: { resetForm: () => void }
  ): Promise<void> => {
    try {
      if (isEditing) {
        await updatePlan({
          variables: {
            plan: toAdGroupBudgetPlanUpdateInput(editingPlan.id as string, values, adgroup),
          },
        })
      } else {
        await createPlan({
          variables: {
            plan: toAdGroupBudgetPlanCreateInput(adGroupId, values, adgroup),
          },
        })
      }
      setSnackbar({ severity: 'success', message: t('commons.saved') })
      resetForm()
      onDone?.()
    } catch (error) {
      setSnackbar({
        severity: 'error',
        message: error instanceof Error ? error.message : t('commons.errorOccurred'),
      })
    }
  }

  return (
    <Box sx={{ mt: isEditing ? 0 : 3 }}>
      {!isEditing && (
        <StyledFormLabel sx={{ display: 'block', mb: 1 }}>
          {t('adgroup.budgetAndDates.budgetPlans.add')}
        </StyledFormLabel>
      )}
      <Formik
        initialValues={initialValues}
        initialTouched={initialTouched}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting, errors, dirty, isValid }) => {
          const formLevelErrors = errors as typeof errors & {
            atLeastOneChange?: string
            autoOpenForbidden?: string
          }
          const formLevelMessages = [formLevelErrors.atLeastOneChange, formLevelErrors.autoOpenForbidden].filter(
            (message): message is string => Boolean(message)
          )

          return (
            <Form>
              <EditionModeContextProvider defaultMode={EditionModes.Edition}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <DateField
                      name="plannedDate"
                      label="adgroup.budgetAndDates.budgetPlans.plannedDate"
                      tooltipText="adgroup.budgetAndDates.budgetPlans.dateHelper"
                      valueFormat="YYYY-MM-DD"
                      minDate={dayjs(tomorrowUtcDateOnly())}
                      required
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
                <BudgetAndDates adgroup={adgroup} isWizard canEdit allowUnchanged fullWidth showSectionTitle={false} />
              </EditionModeContextProvider>
              {dirty &&
                formLevelMessages.map((message) => (
                  <FormHelperText key={message} error>
                    {t(message)}
                  </FormHelperText>
                ))}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                {isEditing && (
                  <Button type="button" variant="outlined" color="secondary" size="small" onClick={() => onDone?.()}>
                    {t('commons.actions.cancel')}
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={isSubmitting || loading || !isValid || !dirty}
                >
                  {t(isEditing ? 'commons.actions.save' : 'commons.actions.add')}
                </Button>
              </Box>
            </Form>
          )
        }}
      </Formik>
    </Box>
  )
}

export default BudgetPlanForm
